import React, { useState, useEffect } from 'react';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CampaignAnalytics } from './CampaignAnalytics';
import { CampaignExport } from './CampaignExport';
import { Alert, AlertDescription } from '../ui/alert';

interface Campaign {
    objective: string;
    isActive: any;
    id: string;
    name: string;
    metrics: {
        spend: number;
        impressions: number;
        clicks: number;
        conversions: number;
        ctr: number;
        cpc: number;
        cpa: number;
        budget?: number;
    };
    dateRange: {
        start: string;
        end: string;
    };
    platform: 'facebook' | 'google' | 'tiktok';
}

interface DateRange {
    from?: Date;
    to?: Date;
}

const DATE_PRESETS = {
    today: {
        label: 'Hoje',
        getDates: () => {
            const today = new Date();
            return { from: today, to: today };
        }
    },
    yesterday: {
        label: 'Ontem',
        getDates: () => {
            const yesterday = subDays(new Date(), 1);
            return { from: yesterday, to: yesterday };
        }
    },
    last7days: {
        label: 'Últimos 7 dias',
        getDates: () => {
            const today = new Date();
            return { from: subDays(today, 7), to: today };
        }
    },
    last14days: {
        label: 'Últimos 14 dias',
        getDates: () => {
            const today = new Date();
            return { from: subDays(today, 14), to: today };
        }
    },
    last30days: {
        label: 'Últimos 30 dias',
        getDates: () => {
            const today = new Date();
            return { from: subDays(today, 30), to: today };
        }
    },
    thisWeek: {
        label: 'Esta Semana',
        getDates: () => ({
            from: startOfWeek(new Date(), { locale: ptBR }),
            to: new Date()
        })
    },
    lastWeek: {
        label: 'Semana Passada',
        getDates: () => {
            const start = startOfWeek(subDays(new Date(), 7), { locale: ptBR });
            const end = endOfWeek(start, { locale: ptBR });
            return { from: start, to: end };
        }
    },
    thisMonth: {
        label: 'Este Mês',
        getDates: () => ({
            from: startOfMonth(new Date()),
            to: new Date()
        })
    },
    lastMonth: {
        label: 'Mês Passado',
        getDates: () => {
            const start = startOfMonth(subMonths(new Date(), 1));
            const end = endOfMonth(start);
            return { from: start, to: end };
        }
    },
    maximum: {
        label: 'Máximo (365 dias)',
        getDates: () => {
            const today = new Date();
            return { from: subDays(today, 365), to: today };
        }
    },
    custom: {
        label: 'Personalizado',
        getDates: () => ({ from: undefined, to: undefined })
    }
};



export const CampaignCard: React.FC<{ campaign: Campaign }> = ({ campaign }) => {
    return (
        <Card className="overflow-hidden hover:shadow-md transition-shadow">
            <CardHeader className="bg-gradient-to-br from-[#FF3366] to-[#FF6B98] text-white mb-6">
                <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-medium">{campaign.name}</CardTitle>
                    {campaign.metrics.budget && (
                        <div className="text-sm">
                            Budget: R$ {campaign.metrics.budget.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                        <p className="text-sm text-gray-500 mb-1">Gasto</p>
                        <p className="text-lg font-semibold">
                            R$ {campaign.metrics.spend.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500 mb-1">Conversões</p>
                        <p className="text-lg font-semibold">
                            {campaign.metrics.conversions.toLocaleString('pt-BR')}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500 mb-1">CPA</p>
                        <p className="text-lg font-semibold">
                            R$ {campaign.metrics.cpa.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500 mb-1">CTR</p>
                        <p className="text-lg font-semibold">
                            {campaign.metrics.ctr.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}%
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-4">
                    <div>
                        <p className="text-sm text-gray-500 mb-1">Impressões</p>
                        <p className="text-lg font-semibold">
                            {campaign.metrics.impressions.toLocaleString('pt-BR')}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500 mb-1">Cliques</p>
                        <p className="text-lg font-semibold">
                            {campaign.metrics.clicks.toLocaleString('pt-BR')}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500 mb-1">CPC</p>
                        <p className="text-lg font-semibold">
                            R$ {campaign.metrics.cpc.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </p>
                    </div>

                    <div className="text-xs text-gray-500 flex items-end">
                        {format(new Date(campaign.dateRange.start), 'dd/MM/yyyy')} -
                        {format(new Date(campaign.dateRange.end), 'dd/MM/yyyy')}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export const CampaignsView: React.FC = () => {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState('meta');
    const [datePreset, setDatePreset] = useState('today');
    const [dateRange, setDateRange] = useState<DateRange>(DATE_PRESETS.today.getDates());
    const [, setIsCalendarOpen] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);

    const [filterState,] = useState({
        search: '',
        objective: 'all',
        spendMin: 0,
        spendMax: 1000,
        cpaMin: 0,
        cpaMax: 100,
        ctrMin: 0,
        ctrMax: 10,
        onlyActive: false
    });

    const fetchCampaigns = async () => {
        try {
            if (!dateRange.from || !dateRange.to) return;

            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const platform = selectedPlatform === 'meta' ? 'facebook' : selectedPlatform;

            const response = await fetch(
                `https://waffle-v2.thenewcc.workers.dev/campaigns/${platform}/` +
                `${format(dateRange.from, 'yyyy-MM-dd')}/${format(dateRange.to, 'yyyy-MM-dd')}`,
                {
                    headers: {
                        'Authorization': `Bearer ${user.token}`
                    }
                }
            );

            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem('user');
                window.location.reload();
                throw new Error('Sessão expirada. Por favor, faça login novamente.');
              }

            if (!response.ok) {
                throw new Error(await response.text());
            }

            const data = await response.json();
            setCampaigns(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch campaigns');
            console.error('Error fetching campaigns:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange.from && dateRange.to) {
            fetchCampaigns();
        }
    }, [selectedPlatform, dateRange]);

    const handleDatePresetChange = (preset: string) => {
        setDatePreset(preset);
        if (preset === 'custom') {
            setIsCalendarOpen(true);
        } else {
            setDateRange(DATE_PRESETS[preset as keyof typeof DATE_PRESETS].getDates());
        }
    };

   
 

    // Apply filters whenever filterState changes
    useEffect(() => {
        const filtered: any = campaigns.filter(campaign => {
            if (filterState.search && 
                !campaign.name.toLowerCase().includes(filterState.search.toLowerCase())) {
                return false;
            }

            if (filterState.objective !== 'all' && campaign.objective !== filterState.objective) {
                return false;
            }

            const metrics = campaign.metrics;
            
            if (metrics.spend < filterState.spendMin || metrics.spend > filterState.spendMax) {
                return false;
            }

            if (metrics.cpa < filterState.cpaMin || metrics.cpa > filterState.cpaMax) {
                return false;
            }

            if (metrics.ctr < filterState.ctrMin || metrics.ctr > filterState.ctrMax) {
                return false;
            }

            if (filterState.onlyActive && !campaign.isActive) {
                return false;
            }

            return true;
        });

        setFilteredCampaigns(filtered);
    }, [filterState, campaigns]);


    const handleExport = () => {
      filteredCampaigns.length > 0 ? filteredCampaigns : campaigns;
        // Export functionality is handled by CampaignExport component
    };


    return (
        <div className="space-y-6">
            {/* Platform Tabs */}
            <Tabs defaultValue={selectedPlatform} className="w-full">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <TabsList className="grid w-full max-w-md grid-cols-3">
                        <TabsTrigger
                            value="meta"
                            onClick={() => setSelectedPlatform('meta')}
                            className="data-[state=active]:bg-[#FF3366] data-[state=active]:text-white"
                        >
                            <span className="text-gray-900 dark:text-gray-100">Meta</span>
                        </TabsTrigger>
                        <TabsTrigger
                            value="tiktok"
                            onClick={() => setSelectedPlatform('tiktok')}
                            className="data-[state=active]:bg-[#FF3366] data-[state=active]:text-white"
                        >
                            <span className="text-gray-900 dark:text-gray-100">TikTok</span>
                        </TabsTrigger>
                        <TabsTrigger
                            value="google"
                            onClick={() => setSelectedPlatform('google')}
                            className="data-[state=active]:bg-[#FF3366] data-[state=active]:text-white"
                        >
                            <span className="text-gray-900 dark:text-gray-100">Google</span>
                        </TabsTrigger>
                    </TabsList>

                     {/* Date Range Selector */}
                     <div className="flex flex-wrap gap-4">
                        <Select value={datePreset} onValueChange={handleDatePresetChange}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Selecione o período" className="text-gray-900 dark:text-gray-100" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(DATE_PRESETS).map(([key, { label }]) => (
                                    <SelectItem key={key} value={key} className="text-gray-900 dark:text-gray-100">{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Analytics Toggle */}
                <div className="flex justify-end mb-4">
                    <Button
                        variant="outline"
                        onClick={() => setShowAnalytics(!showAnalytics)}
                        className="gap-2 border-gray-200 dark:border-gray-700"
                    >
                        <span className="text-gray-900 dark:text-gray-100">{showAnalytics ? 'Esconder Analytics' : 'Mostrar Analytics'}</span>
                    </Button>
                </div>

                {/* Analytics Section */}
                {showAnalytics && (
                    <CampaignAnalytics
                        campaigns={filteredCampaigns.length > 0 ? filteredCampaigns : campaigns}
                        dateRange={{ from: dateRange.from!, to: dateRange.to! }}
                        onExport={handleExport}
                    />
                )}

                {/* Campaigns List */}
                <div className="space-y-4">
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3366]" />
                        </div>
                    ) : error ? (
                        <Alert variant="destructive">
                            <AlertDescription className="text-gray-900 dark:text-gray-100">{error}</AlertDescription>
                        </Alert>
                    ) : filteredCampaigns.length === 0 ? (
                        <Card className="border-gray-200 dark:border-gray-700">
                            <CardContent className="p-8 text-center text-gray-400 dark:text-gray-400">
                                Nenhuma campanha encontrada com os filtros selecionados
                            </CardContent>
                        </Card>
                    ) : (
                        filteredCampaigns.map((campaign) => (
                            <CampaignCard key={campaign.id} campaign={campaign} />
                        ))
                    )}
                </div>
            </Tabs>

            {/* Export Section */}
            <CampaignExport
                campaigns={filteredCampaigns.length > 0 ? filteredCampaigns : campaigns}
                dateRange={{ from: dateRange.from!, to: dateRange.to! }}
            />
        </div>
    );
};